/**
 * OnboardingQuestionnaireEntry — the client's pre-design onboarding questionnaire, completed
 * inside the Client Hub (the "first homework item"). Data-driven from
 * ../onboardingQuestionnaire.js (generated from the Owner Criteria schema, so options match
 * Airtable exactly). On submit it calls onRespond('questionnaire_submitted', { answers }); the
 * journey-respond endpoint creates/updates the project's Owner Criteria record and sets
 * 'Send to Project Concept' = true (firing the seed-concepts automation).
 *
 * answers shape (flat): { [fieldKey]: string | string[], [fieldKey_notes]: string }
 *
 * Save-in-progress (2026-07, database/141): this is a 41-question, 7-step wizard, so losing
 * everything to a refresh or a closed tab before the final submit is a real risk. A debounced
 * autosave (api/journey-questionnaire-progress.js) persists { answers, page } after ~1.5s of
 * inactivity, plus an immediate save on every page transition (Next/Back) so a fast click-through
 * isn't left waiting on the debounce window. entry.criteriaProgress (from buildJourneyPayload,
 * only ever populated pre-submission) rehydrates state on load — the client reopens exactly where
 * they left off. The final submit is unchanged: still the one authoritative write that creates the
 * real Owner Criteria record; the draft is just superseded, never touched again.
 */

import { useEffect, useRef, useState } from 'react'
import { ONBOARDING_QUESTIONNAIRE } from '../onboardingQuestionnaire.js'
import { respond as submitResponse, saveQuestionnaireProgress } from '../../services/journey.js'
import { J, PrimaryButton, GhostButton, DoneChip, ViewOnlyNote } from '../journeyUI.jsx'

const { intro, steps } = ONBOARDING_QUESTIONNAIRE
const TOTAL = steps.length

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 6,
  border: '0.5px solid rgba(7,39,45,0.22)', fontFamily: '"DM Sans", sans-serif', fontSize: 14,
  color: '#07272D', background: '#fff', resize: 'vertical', lineHeight: 1.5,
}

function OptionRow({ selected, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
      padding: '10px 12px', marginBottom: 6, borderRadius: 8, cursor: 'pointer',
      border: '0.5px solid ' + (selected ? '#07272D' : 'rgba(7,39,45,0.18)'),
      background: selected ? 'rgba(7,39,45,0.05)' : '#fff',
      fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: '#07272D',
    }}>
      <span style={{
        flexShrink: 0, width: 16, height: 16, borderRadius: 4,
        border: '1.5px solid ' + (selected ? '#07272D' : 'rgba(7,39,45,0.3)'),
        background: selected ? '#07272D' : 'transparent', color: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
      }}>{selected ? '✓' : ''}</span>
      <span>{children}</span>
    </button>
  )
}

function Question({ q, value, note, onValue, onNote }) {
  const atLimit = q.limit && Array.isArray(value) && value.length >= q.limit
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 15, fontWeight: 600, color: '#07272D', marginBottom: 4, lineHeight: 1.45 }}>
        {q.label}{q.required && <span style={{ color: '#c0392b', marginLeft: 4 }}>*</span>}
      </div>
      {q.limit && (
        <div style={{ ...J.mono, marginBottom: 10 }}>Choose up to {q.limit}{atLimit ? ' — limit reached' : ''}</div>
      )}
      {!q.limit && <div style={{ height: 8 }} />}

      {q.kind === 'longtext' && (
        <textarea rows={3} style={inputStyle} value={value || ''} onChange={e => onValue(e.target.value)} />
      )}

      {(q.kind === 'single') && (q.options || []).map(opt => (
        <OptionRow key={opt} selected={value === opt} onClick={() => onValue(value === opt ? '' : opt)}>{opt}</OptionRow>
      ))}

      {(q.kind === 'multi') && (q.options || []).map(opt => {
        const arr = Array.isArray(value) ? value : []
        const on = arr.includes(opt)
        return (
          <OptionRow key={opt} selected={on} onClick={() => {
            if (on) onValue(arr.filter(x => x !== opt))
            else if (!q.limit || arr.length < q.limit) onValue([...arr, opt])
          }}>{opt}</OptionRow>
        )
      })}

      {q.notes && (
        <textarea rows={2} placeholder="Add a note (optional)" style={{ ...inputStyle, marginTop: 8, fontSize: 13 }}
          value={note || ''} onChange={e => onNote(e.target.value)} />
      )}
    </div>
  )
}

/** Read-only reviewer of the client's raw answers (NOT the synthesized OPC report — that stays
 *  gated until the Pre-design Research milestone is posted). Walks the definition in order. */
function AnswerReview({ data }) {
  const fmt = v => Array.isArray(v) ? v.join(', ') : (v == null ? '' : String(v))
  return (
    <div style={{ marginTop: 26 }}>
      {steps.map(step => (
        <div key={step.key} style={{ marginBottom: 24 }}>
          <div style={{ ...J.eyebrow, marginBottom: 12 }}>{step.title}</div>
          {step.questions.map(q => {
            const v = data[q.key]
            const note = data[q.key + '_notes']
            const has = Array.isArray(v) ? v.length > 0 : !!(v != null && String(v).trim())
            return (
              <div key={q.key} style={{ marginBottom: 14 }}>
                <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: '#07272D', marginBottom: 3, lineHeight: 1.4 }}>{q.label}</div>
                <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14, lineHeight: 1.55, color: has ? '#3a5260' : '#9bb2b7', fontStyle: has ? 'normal' : 'italic' }}>
                  {has ? fmt(v) : 'Not answered'}
                </div>
                {note && String(note).trim() && (
                  <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#7a9aaa', marginTop: 3 }}>Note: {String(note)}</div>
                )}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

export default function OnboardingQuestionnaireEntry({ entry, response, token, previewMode, viewOnly }) {
  // Submit directly through the journey service (not the shell's onRespond, which silently swallows
  // failures) so the Owner Criteria write result is honest — we only show success on a real 2xx.
  // The questionnaire happens in the Pre-design & Planning Phase (it isn't milestone-anchored);
  // show that as the eyebrow rather than a non-existent "Onboarding" phase. A manual phase override
  // (data.phase) wins if set in authoring.
  const phase = entry?.data?.phase || entry?.milestone?.phase || 'Pre-design & Planning Phase'
  const [localDone, setLocalDone] = useState(false)
  // Completed if: submitted this session, a response row exists, OR the project already has an
  // Owner Criteria record (entry.criteriaSubmitted from the server) — the latter guards against a
  // second submission when one was completed earlier (incl. via the old app).
  const submitted = localDone || !!entry.criteriaSubmitted || (response && response.kind === 'questionnaire_submitted')
  // Resume where they left off (database/141) — entry.criteriaProgress is only ever populated
  // pre-submission (see _shareDocs.js#buildJourneyPayload), so a completed questionnaire never
  // accidentally reopens on an old draft.
  const [page, setPage] = useState(() => (entry?.criteriaProgress?.page) || 0)   // 0 = intro; 1..TOTAL = step pages
  const [answers, setAnswers] = useState(() => (entry?.criteriaProgress?.answers) || {})
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [showReview, setShowReview] = useState(false)
  const [saveFlash, setSaveFlash] = useState(false)

  // Best-effort autosave — debounced so mid-page typing doesn't fire a request per keystroke, but
  // still catches someone who never clicks Next again (the exact "cut off in the middle" case).
  // Page transitions (advance()/Back) additionally force an immediate save so a fast click-through
  // doesn't have to wait out the debounce window before it's actually safe to close the tab.
  const saveTimerRef = useRef(null)
  const skipFirstSaveRef = useRef(true)   // don't re-save the answers we just hydrated FROM
  useEffect(() => {
    if (previewMode || submitted || !token || page === 0) return
    if (skipFirstSaveRef.current) { skipFirstSaveRef.current = false; return }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      saveQuestionnaireProgress(token, entry.id, answers, page)
        .then(() => { setSaveFlash(true); setTimeout(() => setSaveFlash(false), 2000) })
        .catch(() => {})
    }, 1500)
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, page])

  if (submitted) {
    // Prefer this session's answers (just submitted); else the stored answers from the server.
    const reviewData = (localDone && Object.keys(answers).length) ? answers : (entry.criteriaAnswers || {})
    const hasAnswers = reviewData && Object.keys(reviewData).length > 0
    return (
      <div>
        <div style={J.eyebrow}>{phase}</div>
        <h2 style={J.title}>{intro.title}</h2>
        <hr style={J.rule} />
        <div style={J.body}>Thank you. Your responses are in, and our team is reviewing them to begin shaping early design concepts for your project.</div>
        <div style={{ marginTop: 24, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <DoneChip>Questionnaire submitted</DoneChip>
          {hasAnswers && (
            <GhostButton onClick={() => setShowReview(v => !v)}>{showReview ? 'Hide answers' : 'View your answers'}</GhostButton>
          )}
        </div>
        {showReview && hasAnswers && <AnswerReview data={reviewData} />}
      </div>
    )
  }

  const setVal = (key, val) => setAnswers(a => ({ ...a, [key]: val }))

  // Intro page
  if (page === 0) {
    return (
      <div>
        <div style={J.eyebrow}>{phase} · {TOTAL} parts</div>
        <h2 style={J.title}>{intro.title}</h2>
        <hr style={J.rule} />
        {intro.paragraphs.map((p, i) => (
          <p key={i} style={{ ...J.body, marginTop: i ? 14 : 0 }}>{p}</p>
        ))}
        <div style={{ marginTop: 28 }}>{viewOnly ? <ViewOnlyNote>The owner fills in this questionnaire.</ViewOnlyNote> : <PrimaryButton onClick={() => setPage(1)}>Begin</PrimaryButton>}</div>
      </div>
    )
  }

  const step = steps[page - 1]

  function firstMissing() {
    for (const q of step.questions) {
      if (!q.required) continue
      const v = answers[q.key]
      if (q.kind === 'multi') { if (!Array.isArray(v) || !v.length) return q.label }
      else if (!v || !String(v).trim()) return q.label
    }
    return null
  }

  // Immediate (non-debounced) save on an explicit page transition — a fast click-through
  // shouldn't have to wait out the 1.5s autosave debounce before it's safe to close the tab.
  function saveNow(targetPage) {
    if (previewMode || !token) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveQuestionnaireProgress(token, entry.id, answers, targetPage)
      .then(() => { setSaveFlash(true); setTimeout(() => setSaveFlash(false), 2000) })
      .catch(() => {})
  }

  async function advance() {
    const missing = firstMissing()
    if (missing) { setErr('Please answer: “' + missing + '”'); return }
    setErr(null)
    if (page < TOTAL) {
      const next = page + 1
      setPage(next); saveNow(next)
      try { window.scrollTo({ top: 0, behavior: 'smooth' }) } catch (_) {}
      return
    }
    // final submit — in the team's Preview overlay this must NEVER write a real Owner Criteria
    // record for the client, so just show the same thank-you/review screen locally.
    if (previewMode) { setLocalDone(true); return }
    if (!token) { setErr('This link is missing its access token. Please reopen your Client Hub link.'); return }
    setBusy(true)
    try {
      await submitResponse(token, entry.id, 'questionnaire_submitted', { answers })
      setLocalDone(true)
    } catch (e) {
      setErr('Could not submit. Please try again.')
      setBusy(false)
    }
  }

  return (
    <div>
      <div style={J.eyebrow}>Part {page} of {TOTAL} · {step.title}</div>
      <h2 style={J.title}>{step.title}</h2>
      {step.intro && <p style={{ ...J.body, marginTop: 10 }}>{step.intro}</p>}
      <hr style={J.rule} />

      {step.questions.map(q => (
        <Question
          key={q.key}
          q={q}
          value={answers[q.key]}
          note={answers[q.key + '_notes']}
          onValue={v => setVal(q.key, v)}
          onNote={v => setVal(q.key + '_notes', v)}
        />
      ))}

      {err && <div style={{ color: '#c0392b', fontFamily: '"DM Sans", sans-serif', fontSize: 13, marginBottom: 14 }}>{err}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        {page > 1 && <GhostButton onClick={() => { setErr(null); const prev = page - 1; setPage(prev); saveNow(prev) }} disabled={busy}>Back</GhostButton>}
        <PrimaryButton onClick={advance} disabled={busy}>
          {busy ? 'Submitting…' : (page < TOTAL ? 'Next' : 'Submit questionnaire')}
        </PrimaryButton>
        {saveFlash && (
          <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#5a8a6b' }}>Progress saved</span>
        )}
      </div>
    </div>
  )
}
